import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import type { KibMultiSelectionOption } from '@chewy/kib-fields-new-react'
import { useDebounce } from 'use-debounce'
import { Defaults } from '@pbt/pbt-ui-components'

import { UserSelectFilterScope } from '~/components/common/inputs/UserSelect'
import { getStateSpecificSuggestionResults } from '~/store/reducers/search'
import { getUsersMap } from '~/store/reducers/users'
import type { SuggestionResult, TeamFilter } from '~/types'

import { KibUserSelectBase } from './KibUserSelectBase'
import {
  buildUserOption,
  mapSuggestionResultsToOptions,
} from './userSelectUtils'

export interface KibUserSelectProps {
  disabled?: boolean
  filterScope?: UserSelectFilterScope
  includeInactive?: boolean
  label?: string
  onChange?: (e: string) => void
  placeHolder: string
  readOnly?: boolean
  required?: boolean
  /**
   * When true, onChange is NOT called when the selection is cleared.
   * Useful when clearing should only reset UI without triggering side effects.
   * @default false
   */
  reset?: boolean
  teamFilter?: TeamFilter
  useInMemory?: boolean
  userSelectStateId: string
  value?: string
}

export const KibUserSelect = ({
  reset = false,
  label,
  userSelectStateId,
  includeInactive = false,
  required = false,
  teamFilter,
  placeHolder,
  disabled,
  onChange,
  value,
  readOnly,
  filterScope = UserSelectFilterScope.MainStaff,
  useInMemory,
}: KibUserSelectProps) => {
  const searchResult = useSelector(
    getStateSpecificSuggestionResults(userSelectStateId),
  )
  const isLoading = searchResult.isSuggestionReceiving
  const searchResults: SuggestionResult[] =
    searchResult.suggestionResults as SuggestionResult[]
  const usersMap = useSelector(getUsersMap)
  const [userOptions, setUserOptions] = useState<KibMultiSelectionOption[]>([])
  const [internalValue, setInternalValue] = useState<KibMultiSelectionOption[]>(
    [],
  )
  const [rawInputValue, setRawInputValue] = useState('')
  const [inputValue] = useDebounce(rawInputValue, Defaults.DEBOUNCE_ACTION_TIME)

  useEffect(() => {
    if (value && !inputValue) {
      const userOption = buildUserOption(usersMap, value)

      if (userOption) {
        setInternalValue([userOption])
        setUserOptions([userOption])
      }
    }
  }, [value])

  useEffect(() => {
    if (searchResults && inputValue) {
      setUserOptions(mapSuggestionResultsToOptions(searchResults))
    }
  }, [searchResults, inputValue])

  const onQuery = (
    v?: string,
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change' && v && v.length) {
      setRawInputValue(v)
    }
  }

  return (
    <KibUserSelectBase
      disabled={disabled}
      filterScope={filterScope}
      includeInactive={includeInactive}
      inputValue={inputValue}
      internalValue={internalValue}
      isLoading={isLoading}
      label={label}
      placeHolder={placeHolder}
      readOnly={readOnly}
      required={required}
      reset={reset}
      setInternalValue={(selectedValues) => setInternalValue(selectedValues)}
      teamFilter={teamFilter}
      useInMemory={useInMemory}
      userOptions={userOptions}
      userSelectStateId={userSelectStateId}
      usersMap={usersMap}
      onChange={onChange}
      onQuery={onQuery}
    />
  )
}
